'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAnimateIn, useStaggeredAnimateIn, useScaleIn } from '@/lib/useAnimateIn';
import { trackEvent } from '@/lib/analytics';
import CTAButton from '@/components/ui/CTAButton';
import ToggleButton from '@/components/ui/ToggleButton';

interface IntroViewProps {
  setCurrentView?: (view: number) => void;
  selectedOptions?: string[];
  setSelectedOptions?: (options: string[]) => void;
  originPosition?: { x: number; y: number };
}

export default function IntroView({ setCurrentView, selectedOptions = [], setSelectedOptions, originPosition }: IntroViewProps) {
  const pathname = usePathname();
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollButtonVisible, setScrollButtonVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Panel animation (fades in first)
  const { style: panelStyle } = useAnimateIn({ delay: 100, translateDistance: -8 });
  
  // Title animation (after panel, with extra beat)
  const { style: titleStyle } = useAnimateIn({ delay: 500 });
  
  // Subtitle animation (slightly after title)
  const { style: subtitleStyle } = useAnimateIn({ delay: 600 });
  
  // Items animation (staggered)
  const { getItemStyle } = useStaggeredAnimateIn({ 
    delay: 800, 
    staggerDelay: 50 
  });
  
  // Continue button animation (scale in with ease-in)
  const { style: buttonStyle, trigger: triggerButton, reset: resetButton } = useScaleIn({ 
    delay: 10,
    duration: 300,
    autoStart: false
  });
  
  // Trigger button animation when it appears
  useEffect(() => {
    if (selectedOptions.length > 0) {
      triggerButton();
    } else {
      resetButton();
    }
  }, [selectedOptions.length, triggerButton, resetButton]);
  
  // Check if user has scrolled away from bottom or Continue button is cut off
  const handleScroll = () => {
    if (!scrollableContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollableContainerRef.current;
    const hasScrollableContent = scrollHeight > clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceFromBottom < 100;
    
    // Check if Continue button exists and is cut off at the bottom
    const continueButton = scrollableContainerRef.current.querySelector('[data-continue-button]') as HTMLElement;
    let continueButtonCutOff = false;
    
    if (continueButton) {
      const containerRect = scrollableContainerRef.current.getBoundingClientRect();
      const buttonRect = continueButton.getBoundingClientRect();
      
      // Check if button is cut off at the bottom (button extends beyond visible container)
      // Add a small buffer (10px) to account for rounding
      continueButtonCutOff = buttonRect.bottom > (containerRect.bottom + 10);
      
      // Also check if button is partially visible but cut off
      const buttonTopVisible = buttonRect.top >= containerRect.top;
      const buttonBottomVisible = buttonRect.bottom <= containerRect.bottom;
      if (buttonTopVisible && !buttonBottomVisible) {
        continueButtonCutOff = true;
      }
    }
    
    // If at the very bottom, always hide the button
    const isAtBottom = distanceFromBottom <= 10;
    
    // Show button if there's scrollable content and:
    // - Not at the bottom, AND
    // - (User is not near bottom OR Continue button is cut off)
    const shouldShow = hasScrollableContent && !isAtBottom && (!isNearBottom || continueButtonCutOff);
    setShowScrollButton(shouldShow);
    
    // Show custom scrollbar when scrolling
    setIsScrolling(true);
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Hide scrollbar after scrolling stops (300ms delay)
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 300);
  };
  
  // Scroll to bottom function
  const scrollToBottom = () => {
    if (!scrollableContainerRef.current) return;
    
    scrollableContainerRef.current.scrollTo({
      top: scrollableContainerRef.current.scrollHeight,
      behavior: 'smooth'
    });
  };
  
  // Add scroll listener and check scroll state
  useEffect(() => {
    const scrollArea = scrollableContainerRef.current;
    if (!scrollArea) return;

    scrollArea.addEventListener('scroll', handleScroll);
    
    // Check initial state with multiple delays to ensure DOM is ready
    const checkInitialState = () => {
      handleScroll();
    };
    
    // Check at multiple intervals to catch content changes
    checkInitialState();
    const timer1 = setTimeout(checkInitialState, 100);
    const timer2 = setTimeout(checkInitialState, 300);
    const timer3 = setTimeout(checkInitialState, 500);
    
    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(() => {
      // Small delay to ensure scrollHeight is updated
      setTimeout(handleScroll, 50);
    });
    resizeObserver.observe(scrollArea);
    
    // Use MutationObserver to detect when Continue button appears/disappears
    const mutationObserver = new MutationObserver(() => {
      // Check immediately when DOM changes
      setTimeout(handleScroll, 50);
    });
    mutationObserver.observe(scrollArea, {
      childList: true,
      subtree: true,
      attributes: false
    });
    
    return () => {
      scrollArea.removeEventListener('scroll', handleScroll);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);
  
  // Check scroll state when content changes (e.g., Continue button appears)
  useEffect(() => {
    // Multiple delays to catch content changes
    const timer1 = setTimeout(() => {
      handleScroll();
    }, 100);
    const timer2 = setTimeout(() => {
      handleScroll();
    }, 300);
    const timer3 = setTimeout(() => {
      handleScroll();
    }, 500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [selectedOptions.length]);
  
  // Scroll button fade-in animation
  useEffect(() => {
    if (showScrollButton) {
      // Add delay before showing the button
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setScrollButtonVisible(true);
          });
        });
      }, 500); // 500ms delay
      return () => clearTimeout(timer);
    } else {
      setScrollButtonVisible(false);
    }
  }, [showScrollButton]);
  
  const scrollButtonStyle: React.CSSProperties = {
    transition: 'opacity 300ms ease-out, transform 300ms ease-out',
    opacity: scrollButtonVisible ? 1 : 0,
    transform: scrollButtonVisible 
      ? 'translateX(-50%) translateY(0)' 
      : 'translateX(-50%) translateY(1.25rem)'
  };

  const CONTACT_OPTIONS = [
  'Better understand my audiences',
  'Modernize my CX', 
  'Improve loyalty',
  'Integrate AI',
  'Harness my data',
  'Create cohesive content'
  ] as const;

  const toggleOption = (option: string) => {
    if (!setSelectedOptions) return;
    
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter(item => item !== option));
    } else if (selectedOptions.length < 3) {
      setSelectedOptions([...selectedOptions, option]);
    }
  };
  
  return (
    <div 
      className="view-intro view-1 md:px-12 md:pt-8 flex flex-col h-full relative"
      style={panelStyle}
    >
      <h2 
        className="heading-3 mb-4"
        style={titleStyle}
      >
        What are you solving for?
      </h2>
      <div 
        className="body-default pt-2 pb-12"
        style={subtitleStyle}
      >
        I want to... (choose up to 3)
      </div>
      <div 
        className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden intro-scroll-area ${isScrolling ? 'scrolling' : ''}`}
        ref={scrollableContainerRef}
      >
        <div className="chat-view-toggle-buttons-container flex flex-wrap gap-3 md:pr-[10%]">
          {CONTACT_OPTIONS.map((option, index) => (
            <div key={option} style={getItemStyle(index)}>
              <ToggleButton
                onClick={() => toggleOption(option)}
                isSelected={selectedOptions.includes(option)}
                disabled={!selectedOptions.includes(option) && selectedOptions.length >= 3}
              >
                {option}
              </ToggleButton>
            </div>
          ))}
        </div>
        
        {selectedOptions.length > 0 && (
          <div 
            className="mt-16 flex justify-center mb-2"
            style={buttonStyle}
            data-continue-button
          >
            <CTAButton 
              onClick={() => {
                // Track continue button click
                const aiSelection = selectedOptions.join('-');
                trackEvent('ai_click', {
                  click_text: 'continue',
                  ai_selection: aiSelection,
                  page_path: pathname
                });
                setCurrentView?.(2);
              }}
              showArrow={false}
            >
              Continue
            </CTAButton>
          </div>
        )}
      </div>
      
      {/* Scroll to bottom button - always render but control visibility */}
      <button
        onClick={scrollToBottom}
        className="scroll-to-bottom-button absolute bottom-8 left-1/2 w-10 h-10 bg-[#FDFEF6] rounded-full shadow-2xl flex items-center justify-center hover:bg-gray-100 transition-all duration-300 border border-gray-300 z-50 pointer-events-none"
        style={{ 
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 0 15px rgba(0, 0, 0, 0.08)',
          ...scrollButtonStyle,
          pointerEvents: showScrollButton && scrollButtonVisible ? 'auto' : 'none'
        }}
        aria-label="Scroll to bottom"
        aria-hidden={!showScrollButton || !scrollButtonVisible}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 14L5 9L6.4 7.6L10 11.2L13.6 7.6L15 9L10 14Z" fill="currentColor"/>
        </svg>
      </button>

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .intro-scroll-area {
          /* Always reserve space for scrollbar to prevent layout shift */
          scrollbar-gutter: stable;
          scrollbar-width: thin; /* Firefox - always thin to reserve space */
          scrollbar-color: transparent transparent; /* Hide by default */
          -ms-overflow-style: none; /* IE and Edge */
          overflow-x: hidden !important; /* Never show horizontal scrollbar */
        }
        
        .intro-scroll-area.scrolling {
          scrollbar-color: rgba(0, 0, 0, 0.3) transparent; /* Show when scrolling */
        }
        
        .intro-scroll-area::-webkit-scrollbar {
          width: 0.375rem;
        }
        
        .intro-scroll-area::-webkit-scrollbar:horizontal {
          display: none; /* Hide horizontal scrollbar */
        }
        
        .intro-scroll-area::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .intro-scroll-area::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 0.1875rem;
          transition: background 0.3s ease;
        }
        
        .intro-scroll-area.scrolling::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.3);
        }
        
        .intro-scroll-area.scrolling::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.5);
        }
      `}</style>
    </div>
  );
}
